import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarDaysIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  GiftIcon,
  MegaphoneIcon,
  PaperClipIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { estoqueService } from '../../services/estoque'
import {
  MARKETING_REQUEST_ATTACHMENT_ACCEPT,
  MARKETING_REQUEST_ATTACHMENT_MAX_BYTES,
  MARKETING_REQUEST_ATTACHMENT_MAX_MB,
  marketingSolicitacaoService,
} from '../../services/marketingSolicitacao'
import type { EstoqueItem } from '../../types/estoque'
import type {
  MarketingRequest,
  MarketingRequestAttachment,
  MarketingRequestItemInput,
  MarketingRequestItemType,
  MarketingRequestPriority,
  MarketingRequestProfileRef,
  MarketingRequestStage,
} from '../../types/marketingSolicitacao'
import EstoqueItemCombobox from '../Estoque/EstoqueItemCombobox'
import {
  ITEM_TYPE_HINTS,
  ITEM_TYPE_LABELS,
  ITEM_TYPE_ORDER,
  PRIORITY_OPTIONS,
  SELECT_CLASS,
  TEXTAREA_CLASS,
  emptyDetailsForType,
  isMarketingRequestPriority,
  validateItemDetails,
} from './solicitacaoUi'

type DraftItem = {
  localId: string
  item_type: MarketingRequestItemType
  details: Record<string, string>
}

export type SolicitacaoFormSubmitPayload = {
  title: string
  notes: string
  neededOn: string
  stageId?: string
  handlerId?: string | null
  priority?: MarketingRequestPriority
  items: MarketingRequestItemInput[]
  files: File[]
  attachmentIdsToDelete: string[]
}

type Props = {
  open: boolean
  busy: boolean
  editing?: MarketingRequest | null
  stages?: MarketingRequestStage[]
  onClose: () => void
  /** Retorne `false` para manter o modal aberto (ex.: fluxo de baixa de estoque). */
  onSubmit: (payload: SolicitacaoFormSubmitPayload) => Promise<boolean | void>
}

function isImageAttachment(mimeType: string | null | undefined, fileName: string): boolean {
  if (mimeType?.startsWith('image/')) return true
  return /\.(jpe?g|png|gif|webp)$/i.test(fileName)
}

function detailsToRecord(details: Record<string, unknown> | null | undefined): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(details || {})) {
    result[key] = value == null ? '' : String(value)
  }
  return result
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(file.name)
}

const TYPE_ICONS: Record<MarketingRequestItemType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  brinde: GiftIcon,
  peca_grafica: ComputerDesktopIcon,
  evento: CalendarDaysIcon,
  material_impresso: PrinterIcon,
  comunicacao: MegaphoneIcon,
  outros: EllipsisHorizontalIcon,
}

function Field({
  label,
  required,
  children,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  hint?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {hint && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  )
}

function ItemFields({
  item,
  stockItems,
  stockLoading,
  onChange,
}: {
  item: DraftItem
  stockItems: EstoqueItem[]
  stockLoading: boolean
  onChange: (details: Record<string, string>) => void
}) {
  const set = (key: string, value: string) => onChange({ ...item.details, [key]: value })
  switch (item.item_type) {
    case 'brinde':
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field
              label="Item"
              required
              hint="Somente itens do tipo Brinde cadastrados no estoque."
            >
              {stockLoading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <LoadingSpinner />
                  Carregando brindes...
                </div>
              ) : stockItems.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  Nenhum brinde ativo cadastrado no estoque. Cadastre em Marketing &gt; Estoque.
                </div>
              ) : (
                <EstoqueItemCombobox
                  items={stockItems}
                  value={item.details.item_id || ''}
                  onChange={(itemId) => {
                    const selected = stockItems.find((s) => s.id === itemId)
                    onChange({
                      ...item.details,
                      item_id: itemId,
                      item: selected ? `${selected.code} — ${selected.name}` : '',
                    })
                  }}
                  required
                  placeholder="Buscar brinde por nome ou código..."
                />
              )}
            </Field>
          </div>
          <Field label="Quantidade" required>
            <Input
              value={item.details.quantidade || ''}
              onChange={(e) => set('quantidade', e.target.value)}
              placeholder="Ex: 50"
            />
          </Field>
          <Field label="Destinatário">
            <Input
              value={item.details.destinatario || ''}
              onChange={(e) => set('destinatario', e.target.value)}
              placeholder="Quem vai receber"
            />
          </Field>
          <Field label="Finalidade">
            <Input
              value={item.details.finalidade || ''}
              onChange={(e) => set('finalidade', e.target.value)}
              placeholder="Para que será usado"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Endereço">
              <Input
                value={item.details.endereco || ''}
                onChange={(e) => set('endereco', e.target.value)}
                placeholder="Endereço de entrega, se houver"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Personalização">
              <textarea
                rows={2}
                className={TEXTAREA_CLASS}
                value={item.details.personalizacao || ''}
                onChange={(e) => set('personalizacao', e.target.value)}
                placeholder="Texto, logo ou detalhe de personalização"
              />
            </Field>
          </div>
        </div>
      )
    case 'peca_grafica':
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Formato" required>
            <Input
              value={item.details.formato || ''}
              onChange={(e) => set('formato', e.target.value)}
              placeholder="Ex: banner, post, e-mail"
            />
          </Field>
          <Field label="Onde será veiculada" required>
            <Input
              value={item.details.onde_sera_veiculada || ''}
              onChange={(e) => set('onde_sera_veiculada', e.target.value)}
              placeholder="Ex: Instagram, site, evento"
            />
          </Field>
          <Field label="Medidas">
            <Input
              value={item.details.medidas || ''}
              onChange={(e) => set('medidas', e.target.value)}
              placeholder="Ex: 1080x1080"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Texto base">
              <textarea
                rows={2}
                className={TEXTAREA_CLASS}
                value={item.details.texto_base || ''}
                onChange={(e) => set('texto_base', e.target.value)}
                placeholder="Texto sugerido para a peça"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Referências">
              <textarea
                rows={2}
                className={TEXTAREA_CLASS}
                value={item.details.referencias || ''}
                onChange={(e) => set('referencias', e.target.value)}
                placeholder="Links ou descrição de referências"
              />
            </Field>
          </div>
        </div>
      )
    case 'evento':
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Data" required>
            <Input type="date" value={item.details.data || ''} onChange={(e) => set('data', e.target.value)} />
          </Field>
          <Field label="Local" required>
            <Input
              value={item.details.local || ''}
              onChange={(e) => set('local', e.target.value)}
              placeholder="Onde acontece"
            />
          </Field>
          <Field label="Público estimado">
            <Input
              value={item.details.publico_estimado || ''}
              onChange={(e) => set('publico_estimado', e.target.value)}
              placeholder="Ex: 80 pessoas"
            />
          </Field>
          <Field label="Responsável no local">
            <Input
              value={item.details.responsavel_no_local || ''}
              onChange={(e) => set('responsavel_no_local', e.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="O que o marketing fornece" required>
              <textarea
                rows={2}
                className={TEXTAREA_CLASS}
                value={item.details.o_que_marketing_fornece || ''}
                onChange={(e) => set('o_que_marketing_fornece', e.target.value)}
                placeholder="Materiais, apoio, divulgação..."
              />
            </Field>
          </div>
        </div>
      )
    case 'material_impresso':
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field
              label="Material"
              required
              hint="Somente itens do tipo Material cadastrados no estoque."
            >
              {stockLoading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <LoadingSpinner />
                  Carregando materiais...
                </div>
              ) : (
                <EstoqueItemCombobox
                  items={stockItems}
                  value={item.details.item_id || ''}
                  onChange={(itemId) => {
                    const selected = stockItems.find((s) => s.id === itemId)
                    onChange({
                      ...item.details,
                      item_id: itemId,
                      material: selected ? `${selected.code} — ${selected.name}` : '',
                    })
                  }}
                  required
                  placeholder="Buscar material por nome ou código..."
                />
              )}
            </Field>
          </div>
          <Field label="Quantidade" required>
            <Input
              value={item.details.quantidade || ''}
              onChange={(e) => set('quantidade', e.target.value)}
              placeholder="Ex: 200"
            />
          </Field>
          <Field label="Prazo" required>
            <Input type="date" value={item.details.prazo || ''} onChange={(e) => set('prazo', e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Destino físico" required>
              <Input
                value={item.details.destino_fisico || ''}
                onChange={(e) => set('destino_fisico', e.target.value)}
                placeholder="Para onde enviar o material"
              />
            </Field>
          </div>
        </div>
      )
    case 'comunicacao':
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Canal pretendido" required>
            <Input
              value={item.details.canal || ''}
              onChange={(e) => set('canal', e.target.value)}
              placeholder="Ex: e-mail, WhatsApp, redes"
            />
          </Field>
          <Field label="Data de veiculação">
            <Input
              type="date"
              value={item.details.data_veiculacao || ''}
              onChange={(e) => set('data_veiculacao', e.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Mensagem principal" required>
              <textarea
                rows={2}
                className={TEXTAREA_CLASS}
                value={item.details.mensagem_principal || ''}
                onChange={(e) => set('mensagem_principal', e.target.value)}
                placeholder="Ideia central da comunicação"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Público">
              <Input
                value={item.details.publico || ''}
                onChange={(e) => set('publico', e.target.value)}
                placeholder="Para quem é a mensagem"
              />
            </Field>
          </div>
        </div>
      )
    case 'outros':
      return (
        <div className="space-y-3">
          <Field label="O que é" required>
            <textarea
              rows={3}
              className={TEXTAREA_CLASS}
              value={item.details.descricao || ''}
              onChange={(e) => set('descricao', e.target.value)}
              placeholder="Descreva o que precisa"
            />
          </Field>
          <Field label="Justificativa" required>
            <textarea
              rows={3}
              className={TEXTAREA_CLASS}
              value={item.details.justificativa || ''}
              onChange={(e) => set('justificativa', e.target.value)}
              placeholder="Por que essa solicitação é necessária"
            />
          </Field>
        </div>
      )
  }
}

const SolicitacaoFormModal: React.FC<Props> = ({
  open,
  busy,
  editing = null,
  stages = [],
  onClose,
  onSubmit,
}) => {
  const isEditing = Boolean(editing?.id)
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [neededOn, setNeededOn] = useState('')
  const [stageId, setStageId] = useState('')
  const [handlerId, setHandlerId] = useState('')
  const [priority, setPriority] = useState<MarketingRequestPriority>('medium')
  const [items, setItems] = useState<DraftItem[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [pendingPreviews, setPendingPreviews] = useState<(string | null)[]>([])
  const [existingAttachments, setExistingAttachments] = useState<MarketingRequestAttachment[]>([])
  const [existingSignedUrls, setExistingSignedUrls] = useState<Record<string, string>>({})
  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = useState<string[]>([])
  const [profiles, setProfiles] = useState<MarketingRequestProfileRef[]>([])
  const [marketingDepartmentConfigured, setMarketingDepartmentConfigured] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stockItems, setStockItems] = useState<EstoqueItem[]>([])
  const [stockLoading, setStockLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hydratedEditingIdRef = useRef<string | null>(null)

  const selectedType = items[0]?.item_type ?? null
  const itemsIssue = useMemo(() => {
    if (!items.length) return 'Informe ao menos um registro.'
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i]
      const validation = validateItemDetails(item.item_type, item.details)
      if (validation) return `${ITEM_TYPE_LABELS[item.item_type]} #${i + 1}: ${validation}`
    }
    return null
  }, [items])
  const canSubmit = useMemo(
    () => title.trim().length > 0 && !itemsIssue && !busy,
    [title, itemsIssue, busy]
  )
  const visibleExistingAttachments = useMemo(
    () => existingAttachments.filter((att) => !attachmentIdsToDelete.includes(att.id)),
    [existingAttachments, attachmentIdsToDelete]
  )

  const handlerOptions = useMemo(() => {
    const list = [...profiles]
    if (editing?.handler && !list.some((p) => p.id === editing.handler!.id)) {
      list.unshift(editing.handler)
    }
    return list
  }, [profiles, editing?.handler])

  useEffect(() => {
    if (!open) {
      setStockItems([])
      return
    }
    const stockItemType =
      selectedType === 'brinde' ? 'brinde' : selectedType === 'material_impresso' ? 'material' : null

    if (!stockItemType) {
      setStockItems([])
      setStockLoading(false)
      return
    }

    let cancelled = false
    const load = async () => {
      setStockLoading(true)
      try {
        const data = await estoqueService.listItens(false, { itemType: stockItemType })
        if (!cancelled) setStockItems(data)
      } catch {
        if (!cancelled) setStockItems([])
      } finally {
        if (!cancelled) setStockLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, selectedType])

  useEffect(() => {
    if (!open || !isEditing) {
      setProfiles([])
      setMarketingDepartmentConfigured(true)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const data = await marketingSolicitacaoService.listActiveProfilesForPicker()
        if (!cancelled) {
          setProfiles(data.profiles)
          setMarketingDepartmentConfigured(Boolean(data.departmentId))
        }
      } catch {
        if (!cancelled) {
          setProfiles([])
          setMarketingDepartmentConfigured(false)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, isEditing])

  useEffect(() => {
    if (!open) {
      hydratedEditingIdRef.current = null
      return
    }
    if (!editing?.id) return
    if (hydratedEditingIdRef.current === editing.id) return
    hydratedEditingIdRef.current = editing.id

    setTitle(editing.title || '')
    setNotes(editing.notes || '')
    setNeededOn(editing.needed_on || '')
    setStageId(editing.stage_id || '')
    setHandlerId(editing.handler_id || '')
    setPriority(isMarketingRequestPriority(editing.priority) ? editing.priority : 'medium')
    setItems(
      (editing.items || []).map((item, index) => ({
        localId: item.id || `edit-${index}`,
        item_type: item.item_type,
        details: detailsToRecord(item.details as Record<string, unknown>),
      }))
    )
    setExistingAttachments(editing.attachments || [])
    setAttachmentIdsToDelete([])
    setPendingFiles([])
    setPendingPreviews((prev) => {
      prev.forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
      return []
    })
    setError(null)
  }, [open, editing])

  useEffect(() => {
    if (!open || !isEditing || existingAttachments.length === 0) {
      setExistingSignedUrls({})
      return
    }
    let cancelled = false
    const load = async () => {
      const entries = await Promise.all(
        existingAttachments.map(async (att) => {
          const url = await marketingSolicitacaoService.getAttachmentUrl(att.file_path)
          return [att.id, url] as const
        })
      )
      if (cancelled) return
      const map: Record<string, string> = {}
      for (const [id, url] of entries) {
        if (url) map[id] = url
      }
      setExistingSignedUrls(map)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, isEditing, existingAttachments])

  if (!open) return null

  const makeDraft = (type: MarketingRequestItemType): DraftItem => ({
    localId: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    item_type: type,
    details: emptyDetailsForType(type),
  })

  const selectType = (type: MarketingRequestItemType) => {
    if (selectedType && selectedType !== type) {
      setError('Esta solicitação já tem um tipo. Remova os registros para escolher outro.')
      return
    }
    if (selectedType === type) return
    setItems([makeDraft(type)])
    setError(null)
  }

  const addAnother = () => {
    if (!selectedType) return
    setItems((prev) => [...prev, makeDraft(selectedType)])
    setError(null)
  }

  const removeItem = (localId: string) => {
    setItems((prev) => prev.filter((item) => item.localId !== localId))
    setError(null)
  }

  const updateDetails = (localId: string, details: Record<string, string>) => {
    setItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, details } : item)))
  }

  const clearForm = () => {
    setTitle('')
    setNotes('')
    setNeededOn('')
    setStageId('')
    setHandlerId('')
    setPriority('medium')
    setItems([])
    setExistingAttachments([])
    setExistingSignedUrls({})
    setAttachmentIdsToDelete([])
    setPendingFiles([])
    setPendingPreviews((prev) => {
      prev.forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
      return []
    })
    setError(null)
    hydratedEditingIdRef.current = null
  }

  const handleClose = () => {
    if (busy) return
    clearForm()
    onClose()
  }

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList?.length) return
    const nextFiles: File[] = []
    const nextPreviews: (string | null)[] = []
    for (const file of Array.from(fileList)) {
      if (file.size > MARKETING_REQUEST_ATTACHMENT_MAX_BYTES) {
        setError(`O arquivo "${file.name}" excede o limite de ${MARKETING_REQUEST_ATTACHMENT_MAX_MB} MB.`)
        continue
      }
      nextFiles.push(file)
      nextPreviews.push(isImageFile(file) ? URL.createObjectURL(file) : null)
    }
    if (nextFiles.length) {
      setPendingFiles((prev) => [...prev, ...nextFiles])
      setPendingPreviews((prev) => [...prev, ...nextPreviews])
      setError(null)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
    setPendingPreviews((prev) => {
      const url = prev[index]
      if (url) URL.revokeObjectURL(url)
      return prev.filter((_, i) => i !== index)
    })
  }

  const removeExistingAttachment = (attachmentId: string) => {
    setAttachmentIdsToDelete((prev) => (prev.includes(attachmentId) ? prev : [...prev, attachmentId]))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const titleTrimmed = title.trim()
    if (!titleTrimmed) {
      setError('Informe o título da solicitação.')
      return
    }
    if (titleTrimmed.length > 200) {
      setError('O título deve ter no máximo 200 caracteres.')
      return
    }
    if (!items.length) {
      setError('Informe ao menos um registro.')
      return
    }
    const types = new Set(items.map((item) => item.item_type))
    if (types.size > 1) {
      setError('Cada solicitação pode ter apenas um tipo.')
      return
    }
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i]
      const validation = validateItemDetails(item.item_type, item.details)
      if (validation) {
        setError(`${ITEM_TYPE_LABELS[item.item_type]} #${i + 1}: ${validation}`)
        return
      }
    }
    if (isEditing && !stageId) {
      setError('Selecione a etapa da solicitação.')
      return
    }
    setError(null)
    const keepOpen = await onSubmit({
      title: titleTrimmed,
      notes,
      neededOn,
      stageId: isEditing ? stageId : undefined,
      handlerId: isEditing ? handlerId || null : undefined,
      priority: isEditing ? priority : undefined,
      items: items.map((item, index) => ({
        item_type: item.item_type,
        details: item.details,
        sort_order: index + 1,
      })),
      files: pendingFiles,
      attachmentIdsToDelete,
    })
    if (keepOpen === false) return
    clearForm()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {isEditing
                  ? `Editar solicitação N° ${editing?.request_number ?? ''}`
                  : 'Nova solicitação'}
              </Heading>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}

            {isEditing && (
              <section className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                      Etapa
                    </Text>
                    <select
                      className={SELECT_CLASS + ' w-full'}
                      value={stageId}
                      onChange={(e) => setStageId(e.target.value)}
                      disabled={busy}
                    >
                      <option value="">Selecione a etapa</option>
                      {stages.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                      Responsável pela tratativa
                    </Text>
                    {!marketingDepartmentConfigured ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                        Configure o parâmetro departamento_marketing em Marketing &gt; Parâmetros.
                      </div>
                    ) : (
                      <select
                        className={SELECT_CLASS + ' w-full'}
                        value={handlerId}
                        onChange={(e) => setHandlerId(e.target.value)}
                        disabled={busy}
                      >
                        <option value="">Sem responsável</option>
                        {handlerOptions.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.name || profile.id}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <div>
                  <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Prioridade
                  </Text>
                  <div className="flex flex-wrap gap-4">
                    {PRIORITY_OPTIONS.map(({ value, label, dotClass }) => (
                      <label key={value} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name="marketing-request-priority"
                          value={value}
                          checked={priority === value}
                          onChange={() => setPriority(value)}
                          disabled={busy}
                          className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotClass}`} aria-hidden="true" />
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </section>
            )}

            <section>
              <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                Título
                <span className="ml-1 text-red-500">*</span>
              </Text>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy}
                maxLength={200}
                required
                placeholder="Ex.: Brindes para o evento de outubro"
              />
            </section>

            <section>
              <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                Para quando
              </Text>
              <Input
                type="date"
                value={neededOn}
                onChange={(e) => setNeededOn(e.target.value)}
                disabled={busy}
              />
              <Text size="xs" color="muted" className="mt-1">
                Opcional — data para a qual esta solicitação se destina.
              </Text>
            </section>

            <section>
              <div className="mb-3">
                <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                  1. O que você precisa?
                </Text>
                <Text size="xs" color="muted" className="mt-0.5">
                  Um tipo por solicitação. Depois você pode incluir vários registros desse tipo.
                </Text>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {ITEM_TYPE_ORDER.map((type) => {
                  const Icon = TYPE_ICONS[type]
                  const selected = selectedType === type
                  const lockedOther = Boolean(selectedType && selectedType !== type)
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => selectType(type)}
                      disabled={busy || lockedOther}
                      aria-pressed={selected}
                      title={
                        lockedOther
                          ? 'Remova os registros atuais para escolher outro tipo'
                          : ITEM_TYPE_HINTS[type]
                      }
                      className={`rounded-xl border p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-45 ${
                        selected
                          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-900/20 dark:ring-primary-400'
                          : 'border-gray-200 bg-white hover:border-primary-500 hover:bg-primary-50/40 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-primary-400 dark:hover:bg-primary-900/10'
                      }`}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        {selected && (
                          <span className="rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                            {items.length}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {ITEM_TYPE_LABELS[type]}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {ITEM_TYPE_HINTS[type]}
                      </div>
                    </button>
                  )
                })}
              </div>
            </section>

            <section>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                    2. Registros
                    <span className="ml-1 text-red-500">*</span>
                  </Text>
                  <Text size="xs" color="muted" className="mt-0.5">
                    {selectedType
                      ? `${items.length} registro(s) de ${ITEM_TYPE_LABELS[selectedType].toLowerCase()}.`
                      : 'Obrigatório. Escolha um tipo acima e informe ao menos um registro.'}
                  </Text>
                </div>
                {selectedType && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addAnother}
                    disabled={busy}
                    className="inline-flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Incluir outro
                  </Button>
                )}
              </div>

              {!items.length ? (
                <div
                  className={`rounded-xl border border-dashed px-4 py-10 text-center ${
                    error
                      ? 'border-red-300 bg-red-50/40 dark:border-red-800 dark:bg-red-900/10'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <p
                    className={`text-sm ${
                      error
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    Informe ao menos um registro.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item, index) => {
                    const Icon = TYPE_ICONS[item.item_type]
                    return (
                      <div
                        key={item.localId}
                        className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
                      >
                        <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700">
                          <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                              <Icon className="h-4 w-4" />
                            </span>
                            <div>
                              <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                                {ITEM_TYPE_LABELS[item.item_type]} #{index + 1}
                              </Text>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(item.localId)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                            title="Remover registro"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Remover
                          </button>
                        </div>
                        <ItemFields
                          item={item}
                          stockItems={stockItems}
                          stockLoading={stockLoading}
                          onChange={(details) => updateDetails(item.localId, details)}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </section>

            <section>
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                    3. Anexos
                  </Text>
                  <Text size="xs" color="muted" className="mt-0.5">
                    Imagens (JPG, PNG, GIF, WebP) ou PDF. Máximo {MARKETING_REQUEST_ATTACHMENT_MAX_MB} MB por arquivo.
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  <PaperClipIcon className="h-4 w-4" />
                  Adicionar arquivo
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={MARKETING_REQUEST_ATTACHMENT_ACCEPT}
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
              </div>

              {visibleExistingAttachments.length === 0 && pendingFiles.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center dark:border-gray-600">
                  <Text size="sm" color="muted">
                    Nenhum anexo selecionado.
                  </Text>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                  {visibleExistingAttachments.map((att) => {
                    const url = existingSignedUrls[att.id]
                    const image = isImageAttachment(att.mime_type, att.file_name)
                    return (
                      <div
                        key={att.id}
                        className="relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900"
                      >
                        {image && url ? (
                          <img src={url} alt={att.file_name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center">
                            <DocumentTextIcon className="h-7 w-7 text-primary-500 dark:text-primary-400" />
                            <p className="line-clamp-2 break-all text-[10px] text-gray-600 dark:text-gray-300">
                              {att.file_name}
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingAttachment(att.id)}
                          disabled={busy}
                          className="absolute right-1 top-1 rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600"
                          title="Remover anexo"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </div>
                    )
                  })}
                  {pendingFiles.map((file, index) => {
                    const preview = pendingPreviews[index]
                    return (
                      <div
                        key={`${file.name}-${index}`}
                        className="relative aspect-square overflow-hidden rounded-lg border border-dashed border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20"
                      >
                        {preview ? (
                          <img src={preview} alt={file.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center">
                            <DocumentTextIcon className="h-7 w-7 text-primary-500 dark:text-primary-400" />
                            <p className="line-clamp-2 break-all text-[10px] text-gray-600 dark:text-gray-300">
                              {file.name}
                            </p>
                            <p className="text-[9px] text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePendingFile(index)}
                          disabled={busy}
                          className="absolute right-1 top-1 rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600"
                          title="Remover anexo"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </section>

            <section>
              <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                4. Observação geral
              </Text>
              <textarea
                rows={2}
                className={TEXTAREA_CLASS}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Opcional — informação que vale para toda a solicitação"
              />
            </section>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
            <p
              className={`text-xs ${
                itemsIssue
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {itemsIssue ||
                (selectedType
                  ? `${ITEM_TYPE_LABELS[selectedType]} · ${items.length} registro(s)`
                  : 'Selecione um tipo para continuar')}
            </p>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={busy}>
                Cancelar
              </Button>
              <Button type="submit" variant="solid" disabled={!canSubmit}>
                {busy ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar solicitação'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SolicitacaoFormModal
